import React, { useMemo } from 'react';
import { ShipInfo } from '@ais-ui/websockets';
import { useGetShipByMmsi } from '@ais-ui/api';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { extractMmsiFromShipInfo } from '../../utils/extractMmsiFromShipInfo';
import { LastPortCallData } from '../LastPortCall';
import {
  PositionType,
  useAisReportAge,
} from '../useAISReportAge';
import { useSelectionState } from '../../../components/SelectionStateProvider';
import { BaseInformationView } from './BaseInformationView';
import { useLengthUnits } from '../../Units/useLengthUnits';

/**
 * BaseInformation - Main window wrapper for vessel base information
 *
 * This component handles data fetching via hooks and passes data to BaseInformationView.
 * For popup windows, use BaseInformationView directly with pre-fetched data.
 */

interface BaseInformationProps {
  shipInfo: ShipInfo;
  onZoomToPosition?: (lat: string, lon: string) => void;
}

export const BaseInformation = ({
  shipInfo,
}: BaseInformationProps) => {
  const permissions = usePermissions();
  const { currentTargetInfo, currentTargetType } = useSelectionState();
  const { checkPositionAge, getAisReportAge } = useAisReportAge();
  const { convertLengthString } = useLengthUnits();

  const hasShipDbPermissions =
    permissions.mdmAdvancedVesselInfo ||
    permissions.mdmRegister ||
    permissions.mdmIntegrity;

  const { data, isLoading } = useGetShipByMmsi(
    extractMmsiFromShipInfo(shipInfo),
    hasShipDbPermissions,
  );

  // Compute last port call data from fetched data
  const lastPortCallData: LastPortCallData | null = useMemo(() => {
    if (!data?.basic?.portCalls || data.basic.portCalls.length === 0) {
      return null;
    }
    const portCall = data.basic.portCalls[data.basic.portCalls.length - 1];
    return {
      portName: portCall.port_name,
      locode: portCall.un_location_code,
      country: portCall.country,
      arrivalTime: portCall.ata,
      departureTime: portCall.atd,
      latitude: portCall.latitude,
      longitude: portCall.longitude,
    };
  }, [data]);

  // Compute extended vessel type
  const extendedVesselType = useMemo(() => {
    return data?.basic?.vesselType ?? null;
  }, [data]);

  // Compute position age statuses
  const positionAgeStatuses = useMemo(() => {
    const lastAgeResult =
      currentTargetInfo && currentTargetType
        ? getAisReportAge(currentTargetInfo, currentTargetType)
        : null;

    const terrestrialAgeResult = shipInfo.lastAisReport
      ? checkPositionAge(shipInfo.lastAisReport, PositionType.Terrestrial)
      : null;

    const satelliteAgeResult = shipInfo.lastAisSatReport
      ? checkPositionAge(shipInfo.lastAisSatReport, PositionType.Satellite)
      : null;

    return {
      last: lastAgeResult?.status,
      terrestrial: terrestrialAgeResult?.status,
      satellite: satelliteAgeResult?.status,
    };
  }, [
    shipInfo.lastAisReport,
    shipInfo.lastAisSatReport,
    currentTargetInfo,
    currentTargetType,
    checkPositionAge,
    getAisReportAge,
  ]);

  return (
    <BaseInformationView
      shipInfo={shipInfo}
      lastPortCallData={lastPortCallData}
      showLastPortCall={hasShipDbPermissions}
      isLoadingLastPortCall={isLoading}
      extendedVesselType={extendedVesselType}
      showExtendedVesselType={hasShipDbPermissions}
      positionAgeStatuses={positionAgeStatuses}
      convertLengthString={convertLengthString}
    />
  );
};

// Re-export the view component for popup usage
export { BaseInformationView } from './BaseInformationView';
export type { BaseInformationViewProps } from './BaseInformationView';
